import React, { useEffect, useMemo, useRef, useState } from "react";
import { normalizeAdminRichText } from "../../utils/richText";

const ALPHABET = "ABCDEFGHIJKLMNÑOPQRSTUVWXYZ".split("");

function getFirstLetter(text) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return "#";
  const letter = trimmed[0].toUpperCase();
  return /[A-ZÁÉÍÓÚÜÑ]/i.test(letter) ? letter.toUpperCase() : "#";
}

function sortByTitle(items, field = "titulo") {
  return [...items].sort((a, b) =>
    String(a?.[field] ?? "").localeCompare(String(b?.[field] ?? ""), "es", {
      sensitivity: "base",
    }),
  );
}

export default function GlosarioPanel({ data }) {
  const terminos = useMemo(
    () => sortByTitle(Array.isArray(data?.terminos) ? data.terminos : []),
    [data],
  );

  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return terminos;
    return terminos.filter((t) =>
      String(t?.titulo ?? "")
        .toLowerCase()
        .includes(q),
    );
  }, [query, terminos]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const term of filtered) {
      const letter = getFirstLetter(term?.titulo);
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter).push(term);
    }
    return map;
  }, [filtered]);

  const availableLetters = useMemo(
    () => new Set([...grouped.keys()]),
    [grouped],
  );

  const selectedTerm = useMemo(() => {
    if (!filtered.length) return null;
    if (selectedId != null) {
      const found = filtered.find((t) => t.id === selectedId);
      if (found) return found;
    }
    return filtered[0];
  }, [filtered, selectedId]);

  useEffect(() => {
    if (filtered.length && selectedId == null) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  useEffect(() => {
    if (selectedId != null && !filtered.some((t) => t.id === selectedId)) {
      setSelectedId(filtered[0]?.id ?? null);
    }
  }, [filtered, selectedId]);

  const activeLetter = useMemo(() => {
    if (!selectedTerm?.titulo) return null;
    return getFirstLetter(selectedTerm.titulo);
  }, [selectedTerm]);

  const scrollToLetter = (letter) => {
    const el = listRef.current?.querySelector(
      `[data-letter="${CSS.escape(letter)}"]`,
    );
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="scm-glosario">
      <div className="scm-glosario__nav">
        <div className="scm-search">
          <i className="bi bi-search scm-search__icon" aria-hidden />
          <input
            type="search"
            className="scm-search__input"
            placeholder="Buscar término…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Buscar término en el glosario"
          />
        </div>

        <div className="scm-alpha" aria-label="Ir a letra">
          {ALPHABET.map((letter) => {
            const enabled = availableLetters.has(letter);
            return (
              <button
                key={letter}
                type="button"
                className={`scm-alpha__btn${
                  !enabled ? " is-disabled" : ""
                }${activeLetter === letter ? " is-active" : ""}`}
                disabled={!enabled}
                onClick={() => scrollToLetter(letter)}
                aria-label={`Ir a la letra ${letter}`}
              >
                {letter}
              </button>
            );
          })}
        </div>

        <div className="scm-glosario__list" ref={listRef}>
          {filtered.length === 0 ? (
            <p className="scm-empty">No se encontraron términos.</p>
          ) : (
            [...grouped.entries()].map(([letter, items]) => (
              <div key={letter} className="scm-glosario__group">
                <div
                  className="scm-glosario__letter"
                  data-letter={letter}
                  aria-hidden
                >
                  {letter}
                </div>
                {items.map((term) => {
                  const active = selectedTerm?.id === term.id;
                  return (
                    <button
                      key={term.id}
                      type="button"
                      className={`scm-glosario__term${active ? " is-active" : ""}`}
                      onClick={() => setSelectedId(term.id)}
                    >
                      {term.titulo}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="scm-glosario__detail">
        {selectedTerm ? (
          <>
            <div className="scm-glosario__detail-head">
              <h3 className="scm-glosario__detail-title">
                {selectedTerm.titulo}
              </h3>
            </div>
            <div
              className="scm-glosario__detail-body rich-text"
              dangerouslySetInnerHTML={{
                __html: normalizeAdminRichText(selectedTerm.definicion),
              }}
            />
          </>
        ) : (
          <p className="scm-empty">No hay términos disponibles.</p>
        )}
      </div>
    </div>
  );
}
